/**
 * ステータスバッジ
 *
 * pill 形状のカラーバッジ。半透明背景。
 * SPEC 7.0 UI デザインガイドライン準拠。
 */
import type { ReactNode } from 'react';
import type { BadgeVariant } from '@/types';

interface BadgeProps {
  /** 色のバリアント */
  variant: BadgeVariant;
  /** バッジ内のコンテンツ */
  children: ReactNode;
  /** サイズ */
  size?: 'sm' | 'md';
}

/**
 * バリアント別の背景色 + テキスト色 (light / dark 両対応)
 *
 * QA-2026-05-08 audit:
 *   旧実装は dark 前提の `bg-{color}-500/20 text-{color}-400` 単独で、light
 *   モードでは pale 背景 + 薄色文字となり「ステータスバッジが読めない」状態。
 *
 *   light モード: pastel 背景 (`bg-{color}-100`) + 濃色文字 (`text-{color}-700`)
 *   dark モード:  従来の dim 背景 (`dark:bg-{color}-500/20`) + bright 文字 (`dark:text-{color}-400`)
 */
const VARIANT_CLASSES: Record<BadgeVariant, string> = {
  blue: 'bg-blue-100 text-blue-700 dark:bg-blue-500/20 dark:text-blue-400',
  green: 'bg-green-100 text-green-700 dark:bg-green-500/20 dark:text-green-400',
  yellow: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-500/20 dark:text-yellow-400',
  red: 'bg-red-100 text-red-700 dark:bg-red-500/20 dark:text-red-400',
  gray: 'bg-gray-100 text-gray-700 dark:bg-gray-500/20 dark:text-gray-400',
  amber: 'bg-amber-100 text-amber-800 dark:bg-amber-500/20 dark:text-amber-400',
  cyan: 'bg-cyan-100 text-cyan-700 dark:bg-cyan-500/20 dark:text-cyan-400',
  purple: 'bg-purple-100 text-purple-700 dark:bg-purple-500/20 dark:text-purple-400',
  indigo: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-500/20 dark:text-indigo-400',
  // 黒地に白字。bg-gray-900 は index.css のライトモード補正で白に置き換わるため bg-black を使う。
  // dark モードでは暗い背景に沈まないよう白地に黒字へ反転する。
  black: 'bg-black text-white dark:bg-gray-100 dark:text-gray-900',
};

/** サイズ別クラス */
const SIZE_CLASSES = {
  sm: 'text-[10px] px-1.5 py-0.5',
  md: 'text-xs px-2 py-0.5',
} as const;

export function Badge({ variant, children, size = 'md' }: BadgeProps) {
  return (
    <span
      className={`inline-flex items-center whitespace-nowrap rounded-full font-medium ${VARIANT_CLASSES[variant]} ${SIZE_CLASSES[size]}`}
    >
      {children}
    </span>
  );
}
